import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  CardGiftcard,
  Block,
} from '@mui/icons-material';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent } from '@ionic/react';
import { 
  addGiftcardTypeAPI, 
  getGiftcardTypesAPI, 
  updateGiftcardTypeAPI, 
  deleteGiftcardTypeAPI 
} from '../../../api/orders/orders';
import { MenuButton } from '../../../components/Menu';

interface GiftcardType {
  id: number;
  name: string;
  code: string;
  business_id: string;
  dateCreated: string;
  hasValueBox: boolean; // New field to determine if giftcard has Value Box
  valueBoxLabel?: string; // Optional label for the Value Box
}

const SetupGiftcardType: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [giftcardTypes, setGiftcardTypes] = useState<GiftcardType[]>([]);
  const [types, setTypes] = useState<GiftcardType[]>([]);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<GiftcardType | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    business_id: 'business_001', // Default business ID
    hasValueBox: false, // Default to having Value Box
    valueBoxLabel: '', // Default empty Giftcard Type
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch giftcard types on component mount
  useEffect(() => {
    fetchGiftcardTypes();
  }, []);

  // Filter giftcard types based on search term
  const filteredTypes = types?.filter(type =>
    type.name.toLowerCase().includes(searchTerm?.toLowerCase()) ||
    type.code.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const fetchGiftcardTypes = async () => {
    setLoading(true);
    try {
      const response = await getGiftcardTypesAPI();
      setGiftcardTypes(response.data);
      setTypes(response.data);
      console.log("Fetched giftcard types:", response.data);
    } catch (error) {
      console.error("Error fetching giftcard types:", error);
      showSnackbar('Failed to load giftcard types', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async () => {
    if (!formData.name) {
      showSnackbar('Name is required', 'error');
      return;
    }

    // If hasValueBox is true but valueBoxLabel is empty, show warning
    // if (!formData.rateBoxLabel ) {
    //   showSnackbar('Giftcard Type is required when Value Box is enabled', 'error');
    //   return;
    // }

    setActionLoading(true);
    try {
      // Prepare data for API
      const submitData = {
        ...formData,
        valueBoxLabel: formData.valueBoxLabel // Only include if hasValueBox is true
      };

      // Call the API to add new giftcard type
      const response = await addGiftcardTypeAPI(submitData);
      console.log(response);
      //return;
      // Refresh the type list
      await fetchGiftcardTypes();
      
      setAddDialogOpen(false);
      resetForm();
      showSnackbar('Giftcard type added successfully', 'success');
    } catch (error) {
      console.error("Error adding giftcard type:", error);
      showSnackbar('Failed to add giftcard type', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditType = async () => {
    if (!selectedType || !formData.name || !formData.symbol) {
      showSnackbar('Name and Code are required', 'error');
      return;
    }

    setActionLoading(true);
    try {
      // Prepare data for update
      const updateData = {
        id: selectedType.id,
        name: formData.name,
        symbol: formData.symbol,
        business_id: selectedType.business_id, // Keep original business_id
        hasValueBox: formData.hasValueBox,
        valueBoxLabel: formData.valueBoxLabel ? formData.valueBoxLabel : formData.rateBoxLabel // Only include if hasValueBox is true
      }

     // console.log(formData)
//return;
      // Call the API to update giftcard type
      const response = await updateGiftcardTypeAPI(updateData);
      
      // Refresh the type list
      await fetchGiftcardTypes();
      
      setEditDialogOpen(false);
      resetForm();
      showSnackbar('Giftcard type updated successfully', 'success');
    } catch (error) {
      console.error("Error updating giftcard type:", error);
      showSnackbar('Failed to update giftcard type', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteType = async () => {
    if (!selectedType) return;

    setActionLoading(true);
    try {
      // Call the API to delete giftcard type
      const response = await deleteGiftcardTypeAPI(selectedType.symbol);
      console.log(selectedType);
      // Refresh the type list
      await fetchGiftcardTypes();
      
      setDeleteDialogOpen(false);
      showSnackbar('Giftcard type deleted successfully', 'success');
    } catch (error) {
      console.error("Error deleting giftcard type:", error);
      showSnackbar('Failed to delete giftcard type', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const openEditDialog = (type: GiftcardType) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      symbol: type.symbol,
      business_id: type.business_id,
      hasValueBox: type.hasValueBox,
      valueBoxLabel: type.rateBoxLabel || '', // Set Giftcard Type if exists
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (type: GiftcardType) => {
    console.log(type)
    setSelectedType(type);
    setFormData({
      name: type.name,
      symbole: type.symbol,
      business_id: type.business_id,
      hasValueBox: type.hasValueBox,
      valueBoxLabel: type.valueBoxLabel || type.rateBoxLabel  // Include Value Box info for display
    });
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      business_id: 'business_001',
      hasValueBox: true, // Default to having Value Box
      valueBoxLabel: '', // Reset Giftcard Type
    });
    setSelectedType(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle Value Box toggle
  const handleValueBoxToggle = (hasValueBox: boolean) => {
    setFormData({
      ...formData,
      hasValueBox,
      // Clear valueBoxLabel when disabling Value Box
      // valueBoxLabel: hasValueBox ? formData.valueBoxLabel : ''
    });
  };

  // Get chip color and icon based on Value Box status
  const getValueBoxChipProps = (hasValueBox: boolean) => {
    if (hasValueBox === 'true' || hasValueBox === true) {
      return {
        color: 'success' as const,
        icon: <CardGiftcard />,
        label: 'With Value Box'
      };
    } else {
      return {
        color: 'default' as const,
        icon: <Block />,
        label: 'No Value Box'
      };
    }
  };

  const withValueBoxCount = types?.filter(type => type.hasValueBox === true || type.hasValueBox === 'true').length;
  const withoutValueBoxCount = types?.filter(type => type.hasValueBox === false || type.hasValueBox === 'false').length;

  return (
    <IonPage className='pushDown'>
      <IonHeader>
        <IonToolbar>  <MenuButton/>
          <IonTitle>Setup Giftcard Types</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Giftcard Types Setup
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage giftcard types and configure value box settings
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Giftcard Types
                  </Typography>
                  <Typography variant="h4">
                    {types?.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    With Value Box
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {withValueBoxCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Without Value Box
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {withoutValueBoxCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>

          {/* Action Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search giftcard types by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openAddDialog}
                >
                  Add New Giftcard Type
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Giftcard Types Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Serial No. </TableCell>
                    <TableCell>Full Name</TableCell>
             
                    <TableCell>Giftcard Label</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading giftcard types...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredTypes?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? 'No giftcard types found matching your search' : 'No giftcard types found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTypes?.map((type, index) => {
                      const chipProps = getValueBoxChipProps(type.hasValueBox);
                      return (
                        <TableRow key={type?.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {type?.name}
                            </Typography>
                          </TableCell>
                        
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              { (type?.rateBoxLabel || 'N/A') }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {type?.dateCreated || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              color="primary"
                              onClick={() => openEditDialog(type)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(type)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Add Giftcard Type Dialog */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Add New Giftcard Type
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setAddDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Giftcard Type Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., iTunes, Apple Card, Amazon, Google Play"
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., ITUNES, APPLE, AMAZON, GOOGLE"
                    helperText="Giftcard type code (will be converted to uppercase)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasValueBox}
                        onChange={(e) => handleValueBoxToggle(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Enable Value Box
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.hasValueBox 
                            ? 'This giftcard type will have a Value Box for amount adjustments'
                            : 'This giftcard type will not have a Value Box'
                          }
                        </Typography>
                      </Box>
                    }
                  />
                </Grid> */}
               
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Giftcard Type Label"
                      value={formData.valueBoxLabel}
                      onChange={(e) => setFormData({ ...formData, valueBoxLabel: e.target.value })}
                      placeholder="e.g., iTunes Code, Apple Card Pin"
                      helperText="Label to display for the Value Box input field"
                      required
                    />
                  </Grid>
                
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleAddType}
                disabled={actionLoading || !formData.name ||   ( !formData.valueBoxLabel)}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Adding...' : 'Add Giftcard Type'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Giftcard Type Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Edit Giftcard Type
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setEditDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Giftcard Type Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    helperText="Giftcard type code (will be converted to uppercase)"
                  />
                </Grid> */}
                {/* <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasValueBox}
                        onChange={(e) => handleValueBoxToggle(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Enable Value Box
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.hasValueBox 
                            ? 'This giftcard type will have a Value Box for amount adjustments'
                            : 'This giftcard type will not have a Value Box'
                          }
                        </Typography>
                      </Box>
                    }
                  />
                </Grid> */}
               
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Giftcard Type Label"
                      value={formData.valueBoxLabel}
                      onChange={(e) => setFormData({ ...formData, valueBoxLabel: e.target.value })}
                      placeholder="e.g., iTunes Code, Apple Card Pin"
                      helperText="Label to display for the Value Box input field"
                      required
                    />
                  </Grid>
              
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleEditType}
                disabled={actionLoading || !formData.name || !formData.symbol || (!formData.valueBoxLabel)}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Updating...' : 'Update Giftcard Type'}  
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Delete Giftcard Type
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setDeleteDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete this giftcard type? This action cannot be undone.
              </Alert>
              <Typography variant="body2">
                <strong>Name:</strong> {formData.name}
              </Typography>
             
             
                <Typography variant="body2">
                  <strong>Giftcard Type Label:</strong> {formData.valueBoxLabel}
                </Typography>
           
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteType}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Deleting...' : 'Delete Giftcard Type'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SetupGiftcardType;